<template>
  <div class="show-card-number">
    <!-- Card Visual -->
    <div class="card-preview-container">
      <div class="card-preview">
        <img src="https://d1a4bxjw35af21.cloudfront.net/images_en/eved-pay-logo.svg" alt="EvedPay Logo">
        <div class="card-number-row">
          <p class="card-number">
            {{ revealed ? formattedCardNumber : '•••• •••• •••• ' + last4 }}
            <span v-if="revealed" class="copy-icon-card" title="Copy card number" @click="copyCardNumber">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            </span>
          </p>
        </div>
        <div class="card-details-row">
          <div class="card-field">
            <p class="card-label"><small>VALID THRU</small></p>
            <p class="card-value">{{ formattedExpirationDate }}</p>
          </div>
          <div class="card-field">
            <p class="card-label"><small>CVV</small></p>
            <p class="card-value">{{ revealed ? cardDetails.CVV : '•••' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- State: INITIAL -->
    <div v-if="state === 'INITIAL'">
      <p class="otp-message">
        {{ otpInstructionMessage }}
      </p>
      <button class="btn-show-card" :disabled="sending" @click="sendOtp">
        {{ sending ? 'Sending...' : 'Show Card Number' }}
      </button>
    </div>

    <!-- State: ENTER_CODE -->
    <div v-if="state === 'ENTER_CODE'">
      <div class="passcode-section">
        <label class="passcode-label">Passcode</label>
        <p class="passcode-hint">{{ otpPasscodeHint }}</p>
        <p v-if="otpErrorMessage" class="otp-error-message">{{ otpErrorMessage }}</p>
        <input
          type="text"
          class="form-control passcode-input"
          v-model="passcode"
          maxlength="6"
          inputmode="numeric"
          pattern="[0-9]*"
          :disabled="verifying || codeExhausted"
          @input="onPasscodeInput"
        />
        <p v-if="!hideResend && canResend" class="resend-link" @click="resendOtp">
          Didn't receive it? <a href="#">Resend Code</a>
        </p>
        <p v-else-if="!hideResend" class="resend-cooldown">
          Resend available in {{ resendCountdown }}s
        </p>
      </div>
    </div>


    <!-- Detail Fields (below card: Account Address, Name on Card only) -->
    <div class="card-detail-fields">
      <div class="detail-field">
        <div class="detail-label">Account Address</div>
        <div v-if="revealed && cardAddress" class="detail-value">
          {{ cardAddress.Address1 }}<br />
          {{ cardAddress.City }}, {{ cardAddress.State && cardAddress.State.Code }} {{ cardAddress.Zip }}
        </div>
        <div v-else class="detail-value"><span class="skeleton"></span></div>
      </div>
      <div class="detail-field">
        <div class="detail-label">Name on Card</div>
        <div class="detail-value">{{ displayNameOnCard }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import otpCardRevealMixin from '@/components/_modules/EvedPay/otpCardRevealMixin'

export default {
  mixins: [otpCardRevealMixin],
  props: {
    card: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      passcode: ''
    }
  },
  computed: {
    last4() {
      return this.card.CardNumber || '****'
    },
    formattedExpirationDate() {
      var exp = this.card.ExpirationDate || ''
      if (!exp) { return '••/••' }
      var match = exp.match(/\/Date\((\d+)\)\//)
      var d = match ? new Date(parseInt(match[1])) : new Date(exp)
      if (isNaN(d.getTime())) { return exp }
      var mm = ('0' + (d.getMonth() + 1)).slice(-2)
      var yy = String(d.getFullYear()).slice(-2)
      return mm + '/' + yy
    },
    displayNameOnCard() {
      var nameOnCard = (this.card.NameOnCard || '').trim()
      return nameOnCard || this.card.Holder || ''
    }
  },
  methods: {
    getCardId() {
      return this.card.Id
    },
    getAccountId() {
      return this.card.AccountId
    },
    getPasscode() {
      return this.passcode
    },
    resetPasscodeInput() {
      this.passcode = ''
    },
    onPasscodeInput() {
      this.passcode = this.passcode.replace(/[^0-9]/g, '').slice(0, 6)
      if (this.passcode.length === 6 && !this.verifying) {
        this.verifyOtp()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.show-card-number {
  padding: 10px;
}

.card-preview-container {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.card-preview {
  background: linear-gradient(315deg, #8C407B 0%, #63417E 100%);
  border-radius: 7px;
  width: 100%;
  aspect-ratio: 312 / 180;
  position: relative;

  img {
    height: auto;
    width: 45%;
    position: absolute;
    top: 20px;
    right: 20px;
  }

  p {
    color: #FFFFFF;
    margin: 0;
    text-shadow: 1px 1px 2px #000000;
    letter-spacing: 0.06em;
  }
}

.card-number-row {
  position: absolute;
  bottom: 60px;
  left: 28px;

  .card-number {
    font-size: 19px;
  }

  .copy-icon-card {
    display: inline-block;
    margin-left: 6px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);
    vertical-align: middle;

    &:hover {
      color: #fff;
    }
  }
}

.card-details-row {
  display: flex;
  position: absolute;
  left: 28px;
  bottom: 15px;
  gap: 20px;

  .card-field {
    .card-label {
      font-size: 9px;
      opacity: 0.8;
    }
    .card-value {
      font-size: 15px;
    }
  }
}

.btn-show-card {
  background-color: #63417E;
  color: #fff;
  border: none;
  border-radius: 3px;
  padding: 8px 24px;
  font-size: 14px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #52346a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.otp-message {
  font-size: 13px;
  color: #666;
  margin-bottom: 15px;
}

.passcode-section {
  margin-bottom: 20px;
}

.passcode-label {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 4px;
}

.passcode-hint {
  color: #333;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 16px;
  margin-bottom: 8px;
}

.otp-error-message {
  color: #dc3545;
  font-size: 13px;
  margin-bottom: 8px;
}

.passcode-input {
  margin-bottom: 10px;
}

.resend-link {
  font-size: 13px;
  margin-top: 10px;
  cursor: pointer;

  a {
    color: #007bff;
  }
}

.resend-cooldown {
  font-size: 13px;
  color: #999;
  margin-top: 10px;
}

.timer-bar {
  margin-bottom: 15px;
}

.timer-text {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  display: block;
}

.timer-track {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
}

.timer-fill {
  height: 100%;
  background: #63417E;
  border-radius: 2px;
  transition: width 1s linear;
}

.card-detail-fields {
  margin-top: 10px;
}

.detail-field {
  padding: 6px 0;

  .detail-label {
    font-size: 13px;
    font-weight: 400;
    color: #888;
    margin-bottom: 1px;
  }

  .detail-value {
    font-size: 13px;
    color: #666;

    .skeleton {
      display: inline-block;
      width: 80px;
      height: 14px;
      background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 4px;
    }
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-list/ShowCardNumber.vue